import { memo, useMemo, useCallback } from 'react';
import { Icon, Input, Slider, DatePicker, Text, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import { useSelector } from 'react-redux';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { FuzzyMap } from 'utils/fuzzy';
import { formatDate } from 'utils/dates';
import {
    CONDITION_OPERATOR_LOCALES,
    CONDITION_TYPE_DEFAULTS,
    CONDITION_TYPE_NO_INPUT,
    getDayWeekOptions,
    getMonthOptions,
} from '../../../../utils';

import SelectHoi from 'components/SelectHoi';
import AdvancedSelect from 'components/AdvancedSelect';
import MultiSelect from 'containers/components/Fields/MultiSelect';
import MultiSelectFuzzy from 'containers/components/Fields/MultiSelectFuzzy';
import Select from 'containers/components/Fields/Select';
import SelectFuzzy from 'containers/components/Fields/SelectFuzzy';
import InputTemplated from '../../components/InputTemplated';

const AutomationBuilderCondition = memo(
    ({
        condition,
        onChange,
        onRemove,
        entityFields,
        fields,
        operators,
        data,
        inputTemplatedProps,
    }) => {
        const theme = useTheme();
        const lang = useSelector((state) => state.config?.userData?.langISOInterface);

        const boolOptions = useMemo(
            () => [
                {
                    label: getLiteral('cfm_label_yes'),
                    value: true,
                },
                {
                    label: getLiteral('cfm_label_no'),
                    value: false,
                },
            ],
            [],
        );

        const operatorOptions = useMemo(() => {
            if (!operators) return [];

            const type = condition?.parameters?.variable?.type;
            if (!type) return [];

            return (
                operators?.[type]
                    ?.filter((operator) => CONDITION_OPERATOR_LOCALES[operator])
                    ?.map((operator) => ({
                        label: getLiteral(CONDITION_OPERATOR_LOCALES[operator]),
                        value: operator,
                    })) || []
            );
        }, [condition?.parameters?.variable?.type, operators]);

        const getOtherFieldInput = useCallback(
            (type, condition) => {
                const options = fields
                    ?.map((field) => {
                        return {
                            label: field.label,
                            key: field.key,
                            options: field.options.filter(
                                (option) =>
                                    option.type === type &&
                                    option?.tableRelated?.table ===
                                        condition?.parameters?.variable?.tableRelated?.table,
                            ),
                        };
                    })
                    .filter((option) => option.options.length > 0);

                const filterOptions = [...new Set(options.map((option) => option.key))]
                    .map((key) => entityFields?.find((entity) => entity.value === key))
                    .filter(Boolean);

                const entityKey = condition?.parameters?.value?.value
                    ?.split?.('.')
                    ?.slice?.(0, -1)
                    ?.join?.('.');

                let valueLabel =
                    entityKey !== 'event.data'
                        ? condition?.parameters?.value?.label +
                          ` (${entityFields?.find((entity) => entity.value === entityKey)?.label})`
                        : condition?.parameters?.value?.label +
                          ` (${getLiteralWithParameters('label_automation_entity_trigger', [
                              getLiteral(data?.scene?.parameters?.entity?.description),
                          ])})`;

                return (
                    <AdvancedSelect
                        filterPlaceholder={getLiteral('label_placeholder_entity')}
                        searchPlaceholder={getLiteral('placeholder_search_field')}
                        menuTitle={getLiteral('cfm_action_add_field')}
                        placeholder={getLiteral('label_placeholder_field')}
                        onChange={onChangeValue('value', condition)}
                        options={options}
                        defaultFilter={filterOptions[0]?.value}
                        filterOptions={filterOptions}
                        value={
                            condition?.parameters?.value
                                ? {
                                      label: valueLabel,
                                      value: condition?.parameters?.value?.value,
                                  }
                                : undefined
                        }
                        isFullWidth
                        useMenuPortal={false}
                    />
                );
            },
            [data?.scene?.parameters?.entity?.description, entityFields, fields, onChangeValue],
        );

        const onChangeField = useCallback(
            (value) => {
                if (!value) {
                    onChange({});
                } else {
                    let operator = condition?.type;
                    operator = CONDITION_TYPE_DEFAULTS[value?.type] || operators[value?.type]?.[0];
                    onChange({
                        type: operator,
                        parameters: {
                            variable: value,
                            value: undefined,
                            valueFrom: undefined,
                            valueTo: undefined,
                        },
                    });
                }
            },
            [condition, onChange, operators],
        );

        const onChangeOperator = useCallback(
            (value) => {
                onChange({
                    ...condition,
                    type: value,
                    parameters: {
                        ...condition.parameters,
                        value: undefined,
                        valueFrom: undefined,
                        valueTo: undefined,
                    },
                });
            },
            [condition, onChange],
        );

        const onChangeValue = useCallback(
            (field = 'value', condition) =>
                (value) => {
                    onChange({
                        ...condition,
                        parameters: {
                            ...condition.parameters,
                            [field]: value,
                        },
                    });
                },
            [onChange],
        );

        const getSelectFieldsByTypes = useCallback(
            (types) => {
                const filteredOptions = inputTemplatedProps.current?.options
                    ?.map((section) => ({
                        ...section,
                        options: section.options?.filter(
                            (option) => !types || types.includes(option.type),
                        ),
                    }))
                    .filter((section) => section.options?.length > 0);

                const filteredFilterOptions = inputTemplatedProps.current?.filterOptions?.filter(
                    (filter) => filteredOptions?.some((section) => section.key === filter.value),
                );

                return {
                    options: filteredOptions || [],
                    filterOptions: filteredFilterOptions || [],
                };
            },
            [inputTemplatedProps],
        );

        const typeInputEl = useMemo(() => {
            const type = condition?.parameters?.variable?.type;
            if (!type) return null;
            const value = condition?.parameters?.value ?? '';
            const operator = condition?.type;
            const key = condition?.parameters?.variable?.key;
            let list, listProps, Component;

            // Check if operator has an input component
            if (CONDITION_TYPE_NO_INPUT.includes(operator)) return null;

            let commonProps;
            switch (type) {
                case 'text':
                    switch (operator) {
                        case 'text_equal_calculated':
                        case 'text_not_equal_calculated':
                            return getOtherFieldInput(type, condition);
                        default:
                            return (
                                <Input
                                    key={condition?.parameters?.variable?.key}
                                    onChange={onChangeValue('value', condition)}
                                    value={value}
                                    placeholder={getLiteral('placeholder_text_field')}
                                    isFullWidth
                                />
                            );
                    }
                case 'int':
                case 'decimal':
                case 'currency':
                    const error =
                        value &&
                        !String(value)?.match(/^(?:[0-9+\-/*()%#\s._]|{{[a-z0-9._\s]+}})*$/gi)
                            ? getLiteral('error_automation_calculated_value_number')
                            : undefined;

                    commonProps = {
                        key,
                        onChange: onChangeValue('value', condition),
                        value: value,
                        placeholder: getLiteral(
                            'label_automation_calculated_value_number_placeholder',
                        ),
                        isFullWidth: true,
                        isNumber: true,
                        insertFieldOptions: getSelectFieldsByTypes(['currency', 'decimal', 'int']),
                        error,
                    };

                    switch (operator) {
                        case 'number_between':
                        case 'number_not_between':
                            const errorFrom =
                                condition?.parameters?.valueFrom &&
                                !String(condition?.parameters?.valueFrom)?.match(
                                    /^(?:[0-9+\-/*()%#\s._]|{{[a-z0-9._\s]+}})*$/gi,
                                )
                                    ? getLiteral('error_automation_calculated_value_number')
                                    : undefined;
                            const errorTo =
                                condition?.parameters?.valueTo &&
                                !String(condition?.parameters?.valueTo)?.match(
                                    /^(?:[0-9+\-/*()%#\s._]|{{[a-z0-9._\s]+}})*$/gi,
                                )
                                    ? getLiteral('error_automation_calculated_value_number')
                                    : undefined;
                            return (
                                <div className="fm-automation-builder__condition__range.templated">
                                    <InputTemplated
                                        {...commonProps}
                                        key={`${key}-from`}
                                        onChange={onChangeValue('valueFrom', condition)}
                                        value={condition?.parameters?.valueFrom}
                                        error={errorFrom}
                                    />
                                    <Text>-</Text>
                                    <InputTemplated
                                        {...commonProps}
                                        key={`${key}-to`}
                                        onChange={onChangeValue('valueTo', condition)}
                                        value={condition?.parameters?.valueTo}
                                        error={errorTo}
                                    />
                                </div>
                            );
                        case 'number_equal_calculated':
                        case 'number_not_equal_calculated':
                            return getOtherFieldInput(type, condition);
                        default:
                            return <InputTemplated {...commonProps} />;
                    }
                case 'percentage':
                    commonProps = {
                        key,
                        onChange: onChangeValue('value', condition),
                        value: value,
                        tipFormatter: (value) => `${value}%`,
                        isFullWidth: true,
                    };
                    switch (operator) {
                        case 'number_between':
                        case 'number_not_between':
                            return (
                                <div className="fm-automation-builder__condition__range">
                                    <Slider
                                        {...commonProps}
                                        key={`${key}-from`}
                                        onChange={onChangeValue('valueFrom', condition)}
                                        value={condition?.parameters?.valueFrom}
                                    />
                                    <Text>-</Text>
                                    <Slider
                                        {...commonProps}
                                        key={`${key}-to`}
                                        onChange={onChangeValue('valueTo', condition)}
                                        value={condition?.parameters?.valueTo}
                                    />
                                </div>
                            );
                        case 'number_equal_calculated':
                        case 'number_not_equal_calculated':
                            return getOtherFieldInput(type, condition);
                        default:
                            return <Slider {...commonProps} />;
                    }
                case 'bit':
                    switch (operator) {
                        case 'bool_equal_calculated':
                        case 'bool_not_equal_calculated':
                            return getOtherFieldInput(type, condition);
                        default:
                            return (
                                <SelectHoi
                                    key={key}
                                    onChange={onChangeValue('value', condition)}
                                    options={boolOptions}
                                    value={value}
                                    usePlainValue
                                    isFullWidth
                                    useMenuPortal={false}
                                />
                            );
                    }
                case 'datetime':
                    switch (operator) {
                        case 'datetime_in_range':
                        case 'datetime_not_in_range':
                            return (
                                <div className="fm-automation-builder__condition__range">
                                    <DatePicker
                                        key={`${key}-from`}
                                        onChange={onChangeValue('valueFrom', condition)}
                                        value={condition?.parameters?.valueFrom}
                                        lang={lang}
                                        calendarButtonLabel={getLiteral('label_today')}
                                        placeholder={getLiteral('label_selectone')}
                                        formatDate={(date) => formatDate(date, 'L')}
                                        isFullWidth
                                    />
                                    <Text>-</Text>
                                    <DatePicker
                                        key={`${key}-to`}
                                        onChange={onChangeValue('valueTo', condition)}
                                        value={condition?.parameters?.valueTo}
                                        lang={lang}
                                        calendarButtonLabel={getLiteral('label_today')}
                                        placeholder={getLiteral('label_selectone')}
                                        formatDate={(date) => formatDate(date, 'L')}
                                        isFullWidth
                                    />
                                </div>
                            );
                        case 'datetime_last_n_days':
                        case 'datetime_next_n_days':
                            return (
                                <Input
                                    key={key}
                                    onChange={onChangeValue('value', condition)}
                                    value={value}
                                    type="integer"
                                    placeholder={getLiteral('placeholder_numeric')}
                                    isFullWidth
                                />
                            );
                        case 'datetime_day_of_week':
                            return (
                                <SelectHoi
                                    key={key}
                                    onChange={onChangeValue('value', condition)}
                                    options={getDayWeekOptions()}
                                    value={value}
                                    usePlainValue
                                    isFullWidth
                                    useMenuPortal={false}
                                />
                            );
                        case 'datetime_month':
                            return (
                                <SelectHoi
                                    key={key}
                                    onChange={onChangeValue('value', condition)}
                                    options={getMonthOptions()}
                                    value={value}
                                    usePlainValue
                                    isFullWidth
                                    useMenuPortal={false}
                                />
                            );
                        case 'datetime_equal_calculated':
                        case 'datetime_not_equal_calculated':
                            return getOtherFieldInput(type, condition);
                        default:
                            return (
                                <DatePicker
                                    key={key}
                                    onChange={onChangeValue()}
                                    value={value}
                                    lang={lang}
                                    calendarButtonLabel={getLiteral('label_today')}
                                    placeholder={getLiteral('label_selectone')}
                                    formatDate={(date) => formatDate(date, 'L')}
                                    isFullWidth
                                />
                            );
                    }
                case 'list':
                    list = condition?.parameters?.variable?.tableRelated?.table;
                    listProps = FuzzyMap[list?.toLowerCase()] || list || {};
                    switch (operator) {
                        case 'list_equal_calculated':
                        case 'list_not_equal_calculated':
                            Component = listProps?.list ? SelectFuzzy : Select;
                            break;
                        default:
                            Component = listProps?.list ? MultiSelectFuzzy : MultiSelect;
                            break;
                    }

                    switch (operator) {
                        case 'list_equal_calculated':
                        case 'list_not_equal_calculated':
                            return getOtherFieldInput(type, condition);
                        default:
                            return (
                                <Component
                                    key={key}
                                    onChange={onChangeValue('value', condition)}
                                    list={condition?.parameters?.variable?.tableRelated?.table}
                                    {...listProps}
                                    value={value}
                                    useMenuPortal={false}
                                    usePlainValue
                                />
                            );
                    }
                case 'multivalue':
                    list = condition?.parameters?.variable?.tableRelated?.table;
                    listProps = FuzzyMap[list?.toLowerCase()] || list || {};
                    Component = listProps?.list ? MultiSelectFuzzy : MultiSelect;

                    switch (operator) {
                        case 'multilist_equal_calculated':
                        case 'multilist_not_equal_calculated':
                            return getOtherFieldInput(type, condition);
                        default:
                            return (
                                <Component
                                    key={key}
                                    onChange={onChangeValue('value', condition)}
                                    list={condition?.parameters?.variable?.tableRelated?.table}
                                    {...listProps}
                                    value={value}
                                    useMenuPortal={false}
                                    usePlainValue
                                />
                            );
                    }
            }
        }, [
            boolOptions,
            condition,
            getOtherFieldInput,
            getSelectFieldsByTypes,
            lang,
            onChangeValue,
        ]);

        const fieldValue = useMemo(() => {
            if (!condition?.parameters?.variable) return undefined;
            const entityKey = condition?.parameters?.variable?.value
                ?.split?.('.')
                ?.slice?.(0, -1)
                ?.join?.('.');

            return {
                ...condition?.parameters?.variable,
                label:
                    entityKey !== 'event.data'
                        ? condition?.parameters?.variable?.label +
                          ` (${entityFields?.find((entity) => entityKey.includes(entity.value))?.label})`
                        : condition?.parameters?.variable?.label +
                          ` (${getLiteralWithParameters('label_automation_entity_trigger', [
                              getLiteral(data?.scene?.parameters?.entity?.description),
                          ])})`,
            };
        }, [
            condition?.parameters?.variable,
            data?.scene?.parameters?.entity?.description,
            entityFields,
        ]);

        return (
            <div className="fm-automation-builder__condition">
                <div className="fm-automation-builder__condition-form">
                    <AdvancedSelect
                        filterPlaceholder={getLiteral('label_placeholder_entity')}
                        searchPlaceholder={getLiteral('placeholder_search_field')}
                        menuTitle={getLiteral('cfm_action_add_field')}
                        placeholder={getLiteral('label_placeholder_field')}
                        onChange={onChangeField}
                        options={fields}
                        defaultFilter={entityFields?.[0]?.value}
                        filterOptions={entityFields}
                        value={fieldValue}
                        isFullWidth
                        useMenuPortal={false}
                    />
                    {condition?.parameters?.variable && (
                        <SelectHoi
                            options={operatorOptions}
                            onChange={onChangeOperator}
                            value={condition.type}
                            isFullWidth
                            useMenuPortal={false}
                            isReadOnly={!operatorOptions?.length}
                            usePlainValue
                            isClearable={false}
                        />
                    )}
                    {typeInputEl}
                </div>
                <Tooltip content={getLiteral('action_delete_condition')} placement="topRight">
                    <span>
                        <Icon
                            className="fm-automation-builder__condition-form-icon"
                            name="less"
                            size="large"
                            onClick={onRemove}
                            options={operatorOptions}
                            color={theme.colors.actionMinor[500]}
                        />
                    </span>
                </Tooltip>
            </div>
        );
    },
);

export default AutomationBuilderCondition;
